import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@scio3/ui';
import { useTranslation } from 'react-i18next';
import { useConfirmStore } from '@/stores/confirm-store';

const DESTRUCTIVE_CLASS = 'bg-destructive text-destructive-foreground hover:bg-destructive/90';

/**
 * Global confirm dialog — render once in App.tsx.
 * Triggered imperatively via `useConfirm()` hook.
 */
export function ConfirmDialog() {
  const { t } = useTranslation();
  const { isOpen, options, close } = useConfirmStore();

  if (!options) return null;

  const showConfirm = options.confirm !== false;
  const showCancel = options.cancel !== false;
  const confirmLabel = (options.confirm && options.confirm.label) || t('OK');
  const confirmVariant = options.confirm && options.confirm.variant;
  const confirmDisabled = !!(options.confirm && options.confirm.disabled);
  const confirmDisabledTooltip = (options.confirm && options.confirm.disabledTooltip) || '';
  const cancelLabel = (options.cancel && options.cancel.label) || t('Cancel');

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && close(false)}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{options.title}</AlertDialogTitle>
          {options.html ? (
            <AlertDialogDescription asChild>
              {/*
                Safe: html option is only used with controlled i18n translation strings,
                never with user-generated content.
              */}
              <div
                className="overflow-hidden [word-break:break-word]"
                dangerouslySetInnerHTML={{ __html: options.description }}
              />
            </AlertDialogDescription>
          ) : (
            <AlertDialogDescription>{options.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          {showCancel && (
            <AlertDialogCancel data-testid="cancel-btn" onClick={() => close(false)}>
              {cancelLabel}
            </AlertDialogCancel>
          )}
          {options.alt && (
            <Button
              variant={options.alt.variant === 'destructive' ? 'destructive' : 'outline'}
              onClick={() => close('alt')}
            >
              {options.alt.label}
            </Button>
          )}
          {showConfirm &&
            (confirmDisabled ? (
              // Disabled primary action — render via Button so the same
              // disabledTooltip treatment as form Save buttons applies (a
              // disabled AlertDialogAction has pointer-events:none and can't
              // host a hover tooltip). No onClick: it can't be confirmed.
              <Button
                data-testid="confirm-btn"
                variant={confirmVariant === 'destructive' ? 'destructive' : 'default'}
                disabled
                disabledTooltip={confirmDisabledTooltip || undefined}
              >
                {confirmLabel}
              </Button>
            ) : (
              <AlertDialogAction
                data-testid="confirm-btn"
                onClick={() => close('confirm')}
                className={confirmVariant === 'destructive' ? DESTRUCTIVE_CLASS : undefined}
              >
                {confirmLabel}
              </AlertDialogAction>
            ))}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
